import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import i18n from '../../i18n';
import type { MenuStoreTemplateMenuRow } from '../../models/menu-store.entity';
import { ALLERGEN, INGREDIENTS } from '../../static/images/dish-base-data';
import defaultMenuImage from '../../static/images/default-menu.png';
import {
    appendMenuStoreOssImageBlurProcess,
    formatMenuStoreBrowseDishTypeLabel,
    parseMenuStoreTemplateMultiKeys,
    resolveMenuStoreDishImage,
    type MenuStoreBrowseDishDetailVariant,
} from './menu-store.helpers';

type MenuStoreDishDetailBrowseRestrictedProps = {
    /** 模板菜品行 */
    dish: MenuStoreTemplateMenuRow;
    /** `locked`：不开放（图模糊 + 敏感区占位）；`open`：同版式展示真实内容 */
    variant: MenuStoreBrowseDishDetailVariant;
};

/**
 * 菜单商店浏览态（未激活）菜品详情：与 Figma 未激活开放/不开放共用同一版式，仅内容与是否模糊图不同。
 */
export function MenuStoreDishDetailBrowseRestricted(props: MenuStoreDishDetailBrowseRestrictedProps): React.ReactElement {
    const { dish, variant } = props;
    /** 描述/过敏源/其他标识是否显示「需激活」占位 */
    const showSensoryLocked = variant === 'locked';
    /** 原始解析图地址 */
    const baseImageUrl = resolveMenuStoreDishImage(dish);
    /** 不开放时在可识别 http(s) 图上追加 OSS 模糊参数 */
    const processedUrl =
        variant === 'locked' && baseImageUrl ? appendMenuStoreOssImageBlurProcess(baseImageUrl) : baseImageUrl;
    /** 首帧展示用图（主图失败时回退默认图） */
    const [imgSrc, setImgSrc] = useState<string>(processedUrl || defaultMenuImage);
    /** 过敏源键列表 */
    const allergenKeys = parseMenuStoreTemplateMultiKeys(dish.allergen);
    /** 其他菜品标识键列表 */
    const ingredientKeys = parseMenuStoreTemplateMultiKeys(dish.other_detail_info);
    /** 「需激活才可查看」统一占位（与 Figma 一致） */
    const needActivateHint = i18n.t('menu_store_browse_dish_need_activate_to_view');
    /** 空字段占位 */
    const emptyMark = i18n.t('menu_store_publisher_unknown');

    React.useEffect(() => {
        setImgSrc(processedUrl || defaultMenuImage);
    }, [processedUrl]);

    /** 开放态描述正文 */
    const descriptionOpenText = (dish.item_description ?? '').trim() || emptyMark;

    return (
        <Box className="menu-store-browse-dish-detail">
            <Box className="menu-store-browse-dish-detail__top">
                <Box className="menu-store-browse-dish-detail__image-wrap">
                    <img
                        className="menu-store-browse-dish-detail__image"
                        src={imgSrc}
                        alt=""
                        onError={() => setImgSrc(defaultMenuImage)}
                    />
                </Box>
                <Box className="menu-store-browse-dish-detail__field-grid">
                    <Box className="menu-store-browse-dish-detail__field-cell">
                        <Typography variant="caption" className="menu-store-browse-dish-detail__field-label" component="span">
                            {i18n.t('menu_library_add_dish_name1')}
                        </Typography>
                        <Typography variant="body1" className="menu-store-browse-dish-detail__field-value" component="p">
                            {(dish.item_name1 ?? '').trim() || emptyMark}
                        </Typography>
                    </Box>
                    <Box className="menu-store-browse-dish-detail__field-cell">
                        <Typography variant="caption" className="menu-store-browse-dish-detail__field-label" component="span">
                            {i18n.t('menu_library_add_dish_name2')}
                        </Typography>
                        <Typography variant="body1" className="menu-store-browse-dish-detail__field-value" component="p">
                            {(dish.item_name2 ?? '').trim() || emptyMark}
                        </Typography>
                    </Box>
                    <Box className="menu-store-browse-dish-detail__field-cell">
                        <Typography variant="caption" className="menu-store-browse-dish-detail__field-label" component="span">
                            {i18n.t('static_dish_page_text_types_of_dishes')}
                        </Typography>
                        <Typography variant="body1" className="menu-store-browse-dish-detail__field-value" component="p">
                            {formatMenuStoreBrowseDishTypeLabel(dish)}
                        </Typography>
                    </Box>
                    <Box className="menu-store-browse-dish-detail__field-cell">
                        <Typography variant="caption" className="menu-store-browse-dish-detail__field-label" component="span">
                            {i18n.t('menu_library_add_dish_portion')}
                        </Typography>
                        <Typography variant="body1" className="menu-store-browse-dish-detail__field-value" component="p">
                            {(dish.unit_1 ?? '').trim() || emptyMark}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box className="menu-store-browse-dish-detail__section">
                <Typography variant="subtitle2" className="menu-store-browse-dish-detail__section-title" component="h3">
                    {i18n.t('static_dish_page_text_dishes_description')}
                </Typography>
                <Box className="menu-store-browse-dish-detail__section-body">
                    <Typography
                        variant="body2"
                        className={
                            showSensoryLocked
                                ? 'menu-store-browse-dish-detail__placeholder-text'
                                : 'menu-store-browse-dish-detail__body-text'
                        }
                        component="p"
                    >
                        {showSensoryLocked ? needActivateHint : descriptionOpenText}
                    </Typography>
                </Box>
            </Box>

            <Box className="menu-store-browse-dish-detail__section">
                <Typography variant="subtitle2" className="menu-store-browse-dish-detail__section-title" component="h3">
                    {i18n.t('menu_store_browse_dish_detail_section_allergen')}
                </Typography>
                <Box className="menu-store-browse-dish-detail__section-body">
                    {showSensoryLocked ? (
                        <Typography variant="body2" className="menu-store-browse-dish-detail__placeholder-text" component="p">
                            {needActivateHint}
                        </Typography>
                    ) : allergenKeys.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" component="p">
                            {emptyMark}
                        </Typography>
                    ) : (
                        <Box className="menu-store-browse-dish-detail__chip-row">
                            {allergenKeys.map((key) => {
                                const meta = ALLERGEN.find((a) => a.key === key);
                                return (
                                    <Box key={key} className="menu-store-browse-dish-detail__chip menu-store-browse-dish-detail__chip--allergen">
                                        {meta ? (
                                            <img className="menu-store-browse-dish-detail__chip-icon" src={meta.icon} alt="" />
                                        ) : null}
                                        <Typography variant="caption" className="menu-store-browse-dish-detail__chip-label" component="span">
                                            {meta ? i18n.t(meta.text) : key}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

            <Box className="menu-store-browse-dish-detail__section">
                <Typography variant="subtitle2" className="menu-store-browse-dish-detail__section-title" component="h3">
                    {i18n.t('menu_library_add_dish_other_tags_section')}
                </Typography>
                <Box className="menu-store-browse-dish-detail__section-body">
                    {showSensoryLocked ? (
                        <Typography variant="body2" className="menu-store-browse-dish-detail__placeholder-text" component="p">
                            {needActivateHint}
                        </Typography>
                    ) : ingredientKeys.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" component="p">
                            {emptyMark}
                        </Typography>
                    ) : (
                        <Box className="menu-store-browse-dish-detail__chip-row">
                            {ingredientKeys.map((key) => {
                                const meta = INGREDIENTS.find((a) => a.key === key);
                                return (
                                    <Box key={key} className="menu-store-browse-dish-detail__chip menu-store-browse-dish-detail__chip--tag">
                                        {meta ? (
                                            <img className="menu-store-browse-dish-detail__chip-icon" src={meta.icon} alt="" />
                                        ) : null}
                                        <Typography variant="caption" className="menu-store-browse-dish-detail__chip-label" component="span">
                                            {meta ? i18n.t(meta.text) : key}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
